import { Pressable, PressableProps, ActivityIndicator, View } from "react-native"
import { cn } from "@src/utils"
import { Text } from "./typography"

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
type ButtonSize = "default" | "sm" | "lg" | "icon"

interface ButtonProps extends PressableProps {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
  loading?: boolean
  children: React.ReactNode
}

export function Button({ className, variant = "default", size = "default", loading = false, disabled, children, ...props }: ButtonProps) {
  const variants: Record<ButtonVariant, string> = {
    default: "bg-blue-600 active:bg-blue-700",
    destructive: "bg-red-500 active:bg-red-600",
    outline: "border border-gray-200 bg-white active:bg-gray-100",
    secondary: "bg-gray-100 active:bg-gray-200",
    ghost: "active:bg-gray-100",
    link: "underline-offset-4 active:underline",
  }

  const sizes: Record<ButtonSize, string> = {
    default: "h-11 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-12 rounded-md px-8",
    icon: "h-10 w-10",
  }

  const textVariants: Record<ButtonVariant, string> = {
    default: "text-white font-medium",
    destructive: "text-white font-medium",
    outline: "text-gray-900 font-medium",
    secondary: "text-gray-900 font-medium",
    ghost: "text-gray-900 font-medium",
    link: "text-blue-600 underline font-medium",
  }

  const baseContainer = "flex-row items-center justify-center rounded-md disabled:opacity-50"

  const containerClass = cn(baseContainer, variants[variant], sizes[size], className)

  const textClass = cn(textVariants[variant], loading && "opacity-0")

  return (
    <Pressable className={containerClass} disabled={disabled || loading} {...props}>
      {typeof children === "string" ? (
        <Text className={textClass} variant={size === "lg" ? "large" : "small"}>
          {children}
        </Text>
      ) : (
        children
      )}

      {loading && (
        <View className="absolute inset-0 flex items-center justify-center">
          <ActivityIndicator color={variant === "outline" || variant === "ghost" || variant === "secondary" ? "black" : "white"} />
        </View>
      )}
    </Pressable>
  )
}
